import {
  Box,
  Button,
  Card,
  CardMedia,
  FormControlLabel,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
// import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { Alert } from "@material-ui/lab";
import { endOfDay, isAfter, parseISO } from "date-fns";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { HEADER_PADDING } from "../../AppLayout";
import { Loader } from "../../components";
import { apiUrl, imageUrl, localImageUrl } from "../../lib";
import { CashOnDeliveryDialog } from "./components";
import storeItemsJson from "./storeItems.json";
import { useParams } from "react-router-dom";


interface StoreItem {
  id: string;
  title: string;
  coverImage: string;
  isLocalImage?: boolean;
  description: string;
  details: string;
  price: number;
  fullPrice: number;
  discountId?: string;
  discountPriceEndDate?: string;
  unit?: string;
}

const storeItems: StoreItem[] = storeItemsJson;

interface Props { }
interface RouteParams {
  itemId?: string;
}

export const Store: React.FC<Props> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { itemId } = useParams<RouteParams>();
  const [imageWidth, setImageWidth] = React.useState(0);
  const imageRef = React.useRef<HTMLBaseElement>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [codDialogOpen, setCodDialogOpen] = React.useState(false);
  const { hash } = useLocation();
  const history = require("react-router-dom").useHistory();

  React.useEffect(() => {
    setImageWidth(imageRef.current ? imageRef.current.clientWidth : 0);
  }, [loaded]);

  // If no itemId, show grid of products
  if (!itemId) {
    return (
      <React.Fragment>
        <Typography
          variant="h1"
          color="primary"
          align={isMobile ? "center" : "left"}
        >
          Store
        </Typography>
        <Box mt={4}>
          {storeItems.length === 0 && (
            <Typography variant="h6" align={isMobile ? "center" : "left"}>
              There are no products available at the moment.
            </Typography>
          )}
          <Grid container spacing={4}>
            {storeItems.map((item) => {
              const hasDiscount = item.discountPriceEndDate;
              const discountPriceEndDate = hasDiscount ? endOfDay(parseISO(hasDiscount)) : null;
              const shouldApplyDiscount = hasDiscount && discountPriceEndDate && !isAfter(new Date(), discountPriceEndDate);
              const isLocalImage = Boolean(item.isLocalImage)

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id}>
                  <Card
                    style={{
                      cursor: "pointer",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onClick={() => history.push(`/store/${item.id}`)}
                    elevation={2}
                  >
                    <CardMedia
                      image={isLocalImage ? localImageUrl(item.coverImage): imageUrl(item.coverImage)}
                      style={{
                        height: 500,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                    <Box p={2} display="flex" flexDirection="column" flexGrow={1}>
                      <Typography variant="h6" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="subtitle1" color="primary">
                        {shouldApplyDiscount ? (
                          <>
                            <del>&euro;{item.fullPrice.toFixed(2)}</del> &nbsp;
                            <strong>&euro;{item.price.toFixed(2)}</strong>
                          </>
                        ) : (
                            <strong>&euro;{item.fullPrice.toFixed(2)}</strong>
                          )}
                      </Typography>
                      <Box flexGrow={1} />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </React.Fragment>
    );
  }

  // Show back button when viewing a single item
  // (itemId is present)
  const handleBack = () => {
    history.push("/store");
  };

  const item = storeItems.find((i) => i.id === itemId);

  if (!item) {
    return <Typography variant="h4">Item not found.</Typography>;
  }

  const image = Boolean(item.isLocalImage) ? localImageUrl(item.coverImage) : imageUrl(item.coverImage);

  const isPaymentSuccessful = hash.startsWith("#success");

  const fullPrice = item.fullPrice;
  const discountPrice = item.price;
  const discountId = item.discountId;
  const discountPriceEndDate = item.discountPriceEndDate
    ? endOfDay(parseISO(item.discountPriceEndDate))
    : null;
  const hasDiscount = item.discountPriceEndDate;
  const shouldApplyDiscount = hasDiscount && discountPriceEndDate && !isAfter(new Date(), discountPriceEndDate);

  return (
    <React.Fragment>
      <Button
        onClick={handleBack}
        variant="outlined"
        color="primary"
        style={{ marginBottom: 24 }}
      >
        ← Back to Store
      </Button>
      {isPaymentSuccessful && (
        <Alert severity="success">
          Thank you for your purchase. An email has been sent to you with the
          order details.
        </Alert>
      )}

      <Typography
        variant="h1"
        color="primary"
        align={isMobile ? "center" : "left"}
      >
        Store
      </Typography>

      <div style={{ paddingTop: isMobile ? undefined : HEADER_PADDING / 2 }}>
        <Grid container spacing={2}>
          <Grid item md={5} sm={12} xs={12}>
            {!loaded ? (
              <Loader imageSrc={image} onImgLoad={(e) => setLoaded(true)} />
            ) : (
                <Card
                  ref={imageRef}
                  elevation={0}
                  style={{
                    height: imageWidth,
                    margin: isMobile ? "auto" : "unset",
                  }}
                >
                  <CardMedia
                    image={image}
                    style={{
                      height: imageWidth,
                      backgroundSize: "contain",
                      backgroundPosition: isMobile ? "center" : "left top",
                    }}
                  />
                </Card>
              )}
          </Grid>

          <Grid item md={7} sm={12} xs={12}>
            <Typography
              variant="h2"
              gutterBottom
              align={isMobile ? "center" : undefined}
            >
              {item.title}
            </Typography>
            <br />
            {/* {item.estimatedDeliveryDate ? <React.Fragment> */}
            {/**/}
            {/*   <Typography */}
            {/*     variant="body1" */}
            {/*     gutterBottom */}
            {/*     align={isMobile ? "center" : undefined} */}
            {/*   > */}
            {/*     <i>Estimated delivery date: {item.estimatedDeliveryDate}</i> */}
            {/*   </Typography> */}
            {/*   <br /> */}
            {/* </React.Fragment> : null} */}
            <Typography
              variant="body1"
              gutterBottom
              align={isMobile ? "justify" : undefined}
            >
              {item.description}
            </Typography>
            <br />
            <Typography
              variant="body1"
              gutterBottom
              align={isMobile ? "justify" : undefined}
            >
              {item.details}
            </Typography>
            <form
              action={`${apiUrl}/create-checkout-session`}
              method="post"
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <input type="hidden" name="itemId" value={item.id} />
              <br /> <br />
              <Typography
                component="span"
                style={{ marginRight: 24, marginBottom: 12 }}
              >
                {shouldApplyDiscount ? (
                  <>
                    {discountId && <input
                      type="hidden"
                      name="couponId"
                      value={discountId}
                    />
                    }
                    <del>&euro;{fullPrice.toFixed(2)}</del> &nbsp;
                    <strong>&euro;{discountPrice.toFixed(2)}</strong>
                  </>
                ) : (
                    <strong>&euro;{fullPrice.toFixed(2)}</strong>
                  )}
              </Typography>
              <FormControlLabel
                label={item.unit || "copies"}
                style={{ marginBottom: 12 }}
                control={
                  <input
                    name="quantity"
                    type="number"
                    defaultValue={1}
                    min={1}
                    max={999}
                    width={150}
                    style={{ marginRight: 8 }}
                  />
                }
              />
              <input name="shipTo" type="hidden" value="MT"/>
              <Button
                role="link"
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                fullWidth={isMobile}
              >
                <ShoppingCartIcon /> &nbsp; Order{" "}
                {isPaymentSuccessful ? "Again" : "Now"}
              </Button>
            </form>
            {/* &nbsp; &nbsp;
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => setCodDialogOpen(true)}
            >
              <LocalAtmIcon /> &nbsp;Cash on delivery
            </Button> */}
          </Grid>
        </Grid>
      </div>

      <CashOnDeliveryDialog
        open={codDialogOpen}
        onClose={() => setCodDialogOpen(false)}
      />
    </React.Fragment>
  );
};
